/**
 * Generic chip/tag input used for symptoms, history, and medications.
 *
 * Features:
 *   - Enter or Comma to add
 *   - Backspace on empty input removes last tag
 *   - Optional autocomplete suggestions from a term set
 *   - Fully keyboard navigable
 */

import * as React from "react";
import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface TagInputProps {
  value: string[];
  onAdd: (value: string) => void;
  onRemove: (index: number) => void;
  placeholder?: string;
  suggestions?: string[];
  maxSuggestions?: number;
  inputRef?: React.Ref<HTMLInputElement>;
  accentClass?: string;
  "aria-label"?: string;
}

export function TagInput({
  value,
  onAdd,
  onRemove,
  placeholder = "Type and press Enter",
  suggestions = [],
  maxSuggestions = 8,
  inputRef,
  accentClass = "bg-primary/10 text-primary border-primary/25",
  "aria-label": ariaLabel,
}: TagInputProps) {
  const [draft, setDraft] = React.useState("");
  const [focused, setFocused] = React.useState(false);
  // The currently highlighted item in the dropdown.
  // 0 = the "Add literal" row at the top
  // 1..N = suggestions
  const [activeIndex, setActiveIndex] = React.useState(0);

  // Refs for every dropdown row so we can auto-scroll the active one
  // into view when it changes via keyboard navigation.
  const itemRefs = React.useRef<Array<HTMLLIElement | null>>([]);

  const filteredSuggestions = React.useMemo(() => {
    const q = draft.trim().toLowerCase();
    if (q.length < 2 || suggestions.length === 0) return [];
    const existing = new Set(value.map((v) => v.toLowerCase()));
    const startsWith: string[] = [];
    const contains: string[] = [];
    for (const term of suggestions) {
      const t = term.toLowerCase();
      if (existing.has(t)) continue;
      if (t === q) continue; // skip exact matches — would be a no-op
      if (t.startsWith(q)) startsWith.push(term);
      else if (t.includes(q)) contains.push(term);
      if (startsWith.length + contains.length >= maxSuggestions * 2) {
        break;
      }
    }
    return [...startsWith, ...contains].slice(0, maxSuggestions);
  }, [draft, suggestions, value, maxSuggestions]);

  // Total options in the dropdown: 1 (literal "Add") + suggestions.length
  const totalOptions = 1 + filteredSuggestions.length;

  // Reset highlight when draft changes — always default to the "Add literal" row
  React.useEffect(() => {
    setActiveIndex(0);
  }, [draft]);

  // Scroll the active item into view when keyboard navigation changes it
  React.useEffect(() => {
    const el = itemRefs.current[activeIndex];
    if (el) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [activeIndex]);

  const commit = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    onAdd(trimmed);
    setDraft("");
    setActiveIndex(0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (activeIndex === 0) {
        // The "Add literal" row is highlighted (default)
        commit(draft);
      } else {
        const suggestion = filteredSuggestions[activeIndex - 1];
        if (suggestion !== undefined) {
          commit(suggestion);
        } else {
          commit(draft);
        }
      }
    } else if (e.key === "Tab" && filteredSuggestions[0] && draft.trim()) {
      // Tab autocompletes to the first suggestion (familiar shell-like UX)
      e.preventDefault();
      commit(filteredSuggestions[0]);
    } else if (e.key === "ArrowDown" && totalOptions > 0) {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, totalOptions - 1));
    } else if (e.key === "ArrowUp" && totalOptions > 0) {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
      onRemove(value.length - 1);
    } else if (e.key === "Escape") {
      setDraft("");
      setActiveIndex(0);
    }
  };

  return (
    <div className="relative">
      <div
        className={cn(
          "border-input flex min-h-9 w-full flex-wrap items-center gap-1 rounded-md border bg-transparent px-2 py-1.5 text-sm shadow-sm",
          "transition-colors",
          focused && "border-ring ring-ring/30 ring-1",
        )}
      >
        <AnimatePresence initial={false}>
          {value.map((tag, idx) => (
            <motion.span
              key={`${tag}-${idx}`}
              layout
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.85 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className={cn(
                "inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium",
                accentClass,
              )}
            >
              <span>{tag}</span>
              <button
                type="button"
                className="focus:ring-ring rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-1"
                onClick={() => onRemove(idx)}
                aria-label={`Remove ${tag}`}
              >
                <X className="h-3 w-3" />
              </button>
            </motion.span>
          ))}
        </AnimatePresence>
        <Input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 120)}
          placeholder={value.length === 0 ? placeholder : ""}
          aria-label={ariaLabel ?? placeholder}
          className="h-6 flex-1 border-0 bg-transparent p-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
        />
      </div>

      <AnimatePresence>
        {focused && draft.trim().length >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.12 }}
            style={{
              backgroundColor: "hsl(var(--popover))",
              color: "hsl(var(--popover-foreground))",
            }}
            className="border-border absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-md border shadow-2xl"
          >
            <ul role="listbox" className="max-h-64 overflow-y-auto py-1">
              {/* "Add literal" row — always first, default highlighted */}
              <li
                ref={(el) => {
                  itemRefs.current[0] = el;
                }}
                role="option"
                aria-selected={activeIndex === 0}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(draft);
                }}
                onMouseEnter={() => setActiveIndex(0)}
                style={
                  activeIndex === 0
                    ? {
                        backgroundColor: "hsl(var(--primary))",
                        color: "hsl(var(--primary-foreground))",
                      }
                    : undefined
                }
                className="hover:bg-muted/60 flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm font-medium transition-colors"
              >
                <span className="flex items-center gap-2">
                  <span className="text-xs opacity-80">Add</span>
                  <span className="font-semibold">&ldquo;{draft.trim()}&rdquo;</span>
                </span>
                <kbd className="font-mono text-[10px] opacity-80">↵</kbd>
              </li>
              {filteredSuggestions.length > 0 && (
                <>
                  <li
                    aria-hidden
                    className="text-muted-foreground px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider"
                  >
                    Suggestions
                  </li>
                  {filteredSuggestions.map((s, i) => {
                    const idx = i + 1;
                    const isActive = idx === activeIndex;
                    return (
                      <li
                        key={s}
                        ref={(el) => {
                          itemRefs.current[idx] = el;
                        }}
                        role="option"
                        aria-selected={isActive}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          commit(s);
                        }}
                        onMouseEnter={() => setActiveIndex(idx)}
                        style={
                          isActive
                            ? {
                                backgroundColor: "hsl(var(--primary))",
                                color: "hsl(var(--primary-foreground))",
                              }
                            : undefined
                        }
                        className="hover:bg-muted/60 cursor-pointer px-3 py-1.5 text-sm font-medium transition-colors"
                      >
                        {s}
                      </li>
                    );
                  })}
                </>
              )}
            </ul>
            <div className="border-border text-muted-foreground border-t px-3 py-1.5 text-[10px]">
              <kbd className="font-mono">↵</kbd> add highlighted ·{" "}
              <kbd className="font-mono">↑↓</kbd> navigate · <kbd className="font-mono">Tab</kbd>{" "}
              first match
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
